import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  loyaltyApi,
  LoyaltySettings,
  EARN_RULE_LABELS,
  BURN_RULE_LABELS,
  ANNOUNCEMENT_TYPE_LABELS,
  type LoyaltyEarnRule,
  type LoyaltyBurnRule,
  type LoyaltyActivityType,
  type PatientPortalAnnouncement,
  type PatientPortalAnnouncementType,
} from '@/api/loyalty';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import { patientApi } from '@/api/patient';

type Tab =
  | 'settings'
  | 'rules'
  | 'analytics'
  | 'announcements'
  | 'campaigns'
  | 'challenges'
  | 'tiers'
  | 'manual-credit';

export default function LoyaltyProgramPage() {
  const [tab, setTab] = useState<Tab>('settings');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['loyalty-settings'],
    queryFn: () => loyaltyApi.getSettings(),
  });

  const { data: analytics, isLoading: loadingAnalytics } = useQuery({
    queryKey: ['loyalty-analytics'],
    queryFn: () => loyaltyApi.getAnalytics(),
    enabled: tab === 'analytics',
  });

  const { data: announcements } = useQuery({
    queryKey: ['loyalty-announcements'],
    queryFn: () => loyaltyApi.listAnnouncements(),
    enabled: tab === 'announcements',
  });

  const { data: campaigns } = useQuery({
    queryKey: ['loyalty-campaigns'],
    queryFn: () => loyaltyApi.listCampaigns(),
    enabled: tab === 'campaigns',
  });

  const { data: challenges } = useQuery({
    queryKey: ['loyalty-challenges'],
    queryFn: () => loyaltyApi.listChallenges(),
    enabled: tab === 'challenges',
  });

  const [form, setForm] = useState<Partial<LoyaltySettings>>({});
  const [annForm, setAnnForm] = useState<{
    message: string;
    type: PatientPortalAnnouncementType;
    linkUrl: string;
    sortOrder: number;
    startsAt: string;
    endsAt: string;
  }>({
    message: '',
    type: 'GENERAL',
    linkUrl: '',
    sortOrder: 0,
    startsAt: '',
    endsAt: '',
  });
  const [campForm, setCampForm] = useState({ title: '', body: '', pointsReward: 0, channel: 'IN_APP' });
  const [chalForm, setChalForm] = useState({ title: '', targetCount: 3, pointsReward: 100, metric: 'OPD_VISITS' });
  const [earnRules, setEarnRules] = useState<LoyaltyEarnRule[]>([]);
  const [burnRules, setBurnRules] = useState<LoyaltyBurnRule[]>([]);
  const [creditPhone, setCreditPhone] = useState('');
  const [creditResults, setCreditResults] = useState<any[]>([]);
  const [creditPatientId, setCreditPatientId] = useState('');
  const [creditPoints, setCreditPoints] = useState('0');
  const [creditDesc, setCreditDesc] = useState('');

  const creditMut = useMutation({
    mutationFn: () => loyaltyApi.creditExtraPoints(creditPatientId, Number(creditPoints), creditDesc || undefined),
    onSuccess: () => {
      toast.success('Points credited');
      setCreditPoints('0');
      setCreditDesc('');
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || 'Credit failed'),
  });

  const saveSettings = useMutation({
    mutationFn: () => loyaltyApi.updateSettings(form),
    onSuccess: () => {
      toast.success('Settings saved');
      qc.invalidateQueries({ queryKey: ['loyalty-settings'] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || 'Save failed'),
  });

  const saveRules = useMutation({
    mutationFn: () => loyaltyApi.updateRules({ earnRules, burnRules }),
    onSuccess: () => {
      toast.success('Earn & burn rules saved');
      qc.invalidateQueries({ queryKey: ['loyalty-settings'] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || 'Save failed'),
  });

  useEffect(() => {
    if (data?.settings) setForm(data.settings);
    if (data?.rules) setEarnRules(data.rules);
    if (data?.burnRules) setBurnRules(data.burnRules);
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'settings', label: 'Settings' },
    { id: 'rules', label: 'Earn & burn rules' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'announcements', label: 'App announcements' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'challenges', label: 'Challenges' },
    { id: 'tiers', label: 'Tiers' },
    { id: 'manual-credit', label: 'Manual credit' },
  ];

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Patient loyalty program</h1>
        <p className="text-muted-foreground mt-1">
          Configure earn/burn rules and announcements for the patient mobile/web app.
        </p>
        <a
          href="/patient-loyalty"
          target="_blank"
          rel="noreferrer"
          className="text-sm text-teal-700 underline mt-2 inline-block"
        >
          Open patient app →
        </a>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              tab === t.id ? 'bg-teal-700 text-white' : 'bg-muted text-muted-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'settings' && (
        <div className="space-y-6">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={!!form.loyaltyEnabled}
              onChange={(e) => setForm((f) => ({ ...f, loyaltyEnabled: e.target.checked }))}
            />
            Enable loyalty program
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={!!form.familyPointsPoolingEnabled}
              onChange={(e) => setForm((f) => ({ ...f, familyPointsPoolingEnabled: e.target.checked }))}
            />
            Family points pooling (linked family members share balance view)
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>OPD visit points</Label>
              <Input
                type="number"
                value={form.defaultOpdVisitPoints ?? 50}
                onChange={(e) => setForm((f) => ({ ...f, defaultOpdVisitPoints: Number(e.target.value) }))}
              />
            </div>
            <div>
              <Label>Referral points</Label>
              <Input
                type="number"
                value={form.defaultReferralPoints ?? 1000}
                onChange={(e) => setForm((f) => ({ ...f, defaultReferralPoints: Number(e.target.value) }))}
              />
            </div>
            <div>
              <Label>IPD admission points</Label>
              <Input
                type="number"
                value={form.defaultIpdPoints ?? 500}
                onChange={(e) => setForm((f) => ({ ...f, defaultIpdPoints: Number(e.target.value) }))}
              />
            </div>
            <div>
              <Label>Pharmacy earn (% of spend)</Label>
              <Input
                type="number"
                step="0.1"
                value={form.defaultPharmacyPercentOfSpend ?? 2}
                onChange={(e) =>
                  setForm((f) => ({ ...f, defaultPharmacyPercentOfSpend: Number(e.target.value) }))
                }
              />
            </div>
            <div>
              <Label>Health / vaccine / app points</Label>
              <div className="grid grid-cols-3 gap-2">
                <Input
                  type="number"
                  value={form.defaultHealthCheckupPoints ?? 200}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, defaultHealthCheckupPoints: Number(e.target.value) }))
                  }
                />
                <Input
                  type="number"
                  value={form.defaultVaccinationPoints ?? 100}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, defaultVaccinationPoints: Number(e.target.value) }))
                  }
                />
                <Input
                  type="number"
                  value={form.defaultAppEngagementPoints ?? 25}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, defaultAppEngagementPoints: Number(e.target.value) }))
                  }
                />
              </div>
            </div>
            <div>
              <Label>Referrer promo credit (₹)</Label>
              <Input
                type="number"
                value={(form as { referralPromoCreditReferrer?: number }).referralPromoCreditReferrer ?? 100}
                onChange={(e) =>
                  setForm((f) => ({ ...f, referralPromoCreditReferrer: Number(e.target.value) }))
                }
              />
            </div>
            <div>
              <Label>Referred patient welcome promo (₹)</Label>
              <Input
                type="number"
                value={(form as { referralPromoCreditReferred?: number }).referralPromoCreditReferred ?? 50}
                onChange={(e) =>
                  setForm((f) => ({ ...f, referralPromoCreditReferred: Number(e.target.value) }))
                }
              />
            </div>
            <div>
              <Label>Max redemption % of bill</Label>
              <Input
                type="number"
                value={form.maxRedemptionPercent ?? 20}
                onChange={(e) =>
                  setForm((f) => ({ ...f, maxRedemptionPercent: Number(e.target.value) }))
                }
              />
            </div>
            <div>
              <Label>Points per ₹1</Label>
              <Input
                type="number"
                step="0.01"
                value={form.pointsPerRupee ?? 0.1}
                onChange={(e) => setForm((f) => ({ ...f, pointsPerRupee: Number(e.target.value) }))}
              />
            </div>
            <div>
              <Label>App display name</Label>
              <Input
                value={form.appDisplayName ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, appDisplayName: e.target.value }))}
              />
            </div>
          </div>
          <Button onClick={() => saveSettings.mutate()} disabled={saveSettings.isPending}>
            Save settings
          </Button>
        </div>
      )}

      {tab === 'rules' && (
        <div className="space-y-8">
          <section>
            <h3 className="font-semibold mb-3">Earn rules</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Auto-applied when bills are paid, IPD is admitted, referrals complete, or patients use the app.
            </p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2">Activity</th>
                    <th className="text-left p-2">Fixed pts</th>
                    <th className="text-left p-2">% of spend</th>
                    <th className="text-left p-2">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {earnRules
                    .filter((r) => r.activityType !== 'MANUAL')
                    .map((r, i) => (
                      <tr key={r.id} className="border-t">
                        <td className="p-2">
                          {EARN_RULE_LABELS[r.activityType as LoyaltyActivityType] ?? r.activityType}
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            className="h-8 w-24"
                            value={r.points}
                            onChange={(e) => {
                              const next = [...earnRules];
                              next[i] = { ...r, points: Number(e.target.value) };
                              setEarnRules(next);
                            }}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            step="0.1"
                            className="h-8 w-24"
                            value={r.percentOfSpend}
                            onChange={(e) => {
                              const idx = earnRules.findIndex((x) => x.id === r.id);
                              const next = [...earnRules];
                              next[idx] = { ...r, percentOfSpend: Number(e.target.value) };
                              setEarnRules(next);
                            }}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={r.isActive}
                            onChange={(e) => {
                              const idx = earnRules.findIndex((x) => x.id === r.id);
                              const next = [...earnRules];
                              next[idx] = { ...r, isActive: e.target.checked };
                              setEarnRules(next);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
          <section>
            <h3 className="font-semibold mb-3">Burn rules (redemption)</h3>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2">Purpose</th>
                    <th className="text-left p-2">Fixed pts cost</th>
                    <th className="text-left p-2">Max % of bill</th>
                    <th className="text-left p-2">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {burnRules.map((r, i) => (
                    <tr key={r.id} className="border-t">
                      <td className="p-2">{BURN_RULE_LABELS[r.purpose]}</td>
                      <td className="p-2">
                        <Input
                          type="number"
                          className="h-8 w-24"
                          placeholder="—"
                          value={r.pointsCost ?? ''}
                          onChange={(e) => {
                            const next = [...burnRules];
                            next[i] = {
                              ...r,
                              pointsCost: e.target.value === '' ? null : Number(e.target.value),
                            };
                            setBurnRules(next);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          className="h-8 w-24"
                          placeholder="Global"
                          value={r.maxRedemptionPercent ?? ''}
                          onChange={(e) => {
                            const next = [...burnRules];
                            next[i] = {
                              ...r,
                              maxRedemptionPercent:
                                e.target.value === '' ? null : Number(e.target.value),
                            };
                            setBurnRules(next);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={r.isActive}
                          onChange={(e) => {
                            const next = [...burnRules];
                            next[i] = { ...r, isActive: e.target.checked };
                            setBurnRules(next);
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <Button onClick={() => saveRules.mutate()} disabled={saveRules.isPending}>
            Save earn & burn rules
          </Button>
        </div>
      )}

      {tab === 'analytics' && (
        <div className="space-y-4">
          {loadingAnalytics ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : analytics ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard label="Points liability" value={analytics.pointsLiability} />
              <StatCard label="Wallets" value={analytics.wallets} />
              <StatCard label="Repeat visit %" value={`${analytics.repeatVisitRatePct}%`} />
              <StatCard label="Referral conversion %" value={`${analytics.referralConversionPct}%`} />
              <StatCard label="Earned (30d pts)" value={analytics.earnPointsLast30d} />
              <StatCard label="Challenges done" value={analytics.challengeCompletions} />
            </div>
          ) : null}
          <Button variant="outline" onClick={() => loyaltyApi.runMembershipJob().then(() => toast.success('Membership tiers recalculated'))}>
            Run membership qualification now
          </Button>
        </div>
      )}

      {tab === 'manual-credit' && (
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold">Credit extra points (admin)</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Search a patient by phone, then credit bonus points with a reason.
            </p>
          </div>

          <div className="rounded-lg border p-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Label>Patient phone</Label>
                <Input
                  placeholder="10-digit mobile"
                  value={creditPhone}
                  onChange={(e) => setCreditPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    if (creditPhone.length !== 10) {
                      toast.error('Enter valid 10-digit phone');
                      return;
                    }
                    const res = await patientApi.getPatientByPhone(creditPhone);
                    setCreditResults(res ?? []);
                    setCreditPatientId(res?.[0]?.id ?? '');
                  }}
                >
                  Search
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Label>Select patient</Label>
                <select
                  className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm"
                  value={creditPatientId}
                  onChange={(e) => setCreditPatientId(e.target.value)}
                >
                  <option value="">Select…</option>
                  {creditResults.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name || p.patientName || `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.id} ({p.phone})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Points</Label>
                <Input type="number" value={creditPoints} onChange={(e) => setCreditPoints(e.target.value)} />
              </div>
            </div>

            <div>
              <Label>Description / reason</Label>
              <Input
                placeholder="Eg. goodwill, complaint resolution, anniversary bonus"
                value={creditDesc}
                onChange={(e) => setCreditDesc(e.target.value)}
              />
            </div>

            <Button
              onClick={() => {
                if (!creditPatientId) return toast.error('Select a patient');
                if (!Number.isFinite(Number(creditPoints)) || Number(creditPoints) <= 0) {
                  toast.error('Enter points > 0');
                  return;
                }
                creditMut.mutate();
              }}
              disabled={creditMut.isPending}
            >
              Credit points
            </Button>
          </div>
        </div>
      )}

      {tab === 'announcements' && (
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Short messages scroll on the patient app home welcome banner (discounts, health camps,
            promotions). Only active items within their date range are shown.
          </p>
          <div className="border rounded-lg p-4 space-y-3 bg-white">
            <h3 className="font-semibold">New announcement</h3>
            <div>
              <Label>Message (shown in scrolling bar)</Label>
              <Input
                placeholder="e.g. 20% off health checkup this week — book at reception"
                value={annForm.message}
                maxLength={280}
                onChange={(e) => setAnnForm((f) => ({ ...f, message: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">{annForm.message.length}/280</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Type</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={annForm.type}
                  onChange={(e) =>
                    setAnnForm((f) => ({
                      ...f,
                      type: e.target.value as PatientPortalAnnouncementType,
                    }))
                  }
                >
                  {(Object.keys(ANNOUNCEMENT_TYPE_LABELS) as PatientPortalAnnouncementType[]).map(
                    (k) => (
                      <option key={k} value={k}>
                        {ANNOUNCEMENT_TYPE_LABELS[k]}
                      </option>
                    )
                  )}
                </select>
              </div>
              <div>
                <Label>Sort order (lower = first)</Label>
                <Input
                  type="number"
                  value={annForm.sortOrder}
                  onChange={(e) =>
                    setAnnForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 0 }))
                  }
                />
              </div>
            </div>
            <div>
              <Label>Link URL (optional)</Label>
              <Input
                placeholder="https://..."
                value={annForm.linkUrl}
                onChange={(e) => setAnnForm((f) => ({ ...f, linkUrl: e.target.value }))}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Starts (optional)</Label>
                <Input
                  type="datetime-local"
                  value={annForm.startsAt}
                  onChange={(e) => setAnnForm((f) => ({ ...f, startsAt: e.target.value }))}
                />
              </div>
              <div>
                <Label>Ends (optional)</Label>
                <Input
                  type="datetime-local"
                  value={annForm.endsAt}
                  onChange={(e) => setAnnForm((f) => ({ ...f, endsAt: e.target.value }))}
                />
              </div>
            </div>
            <Button
              onClick={() => {
                if (!annForm.message.trim()) {
                  toast.error('Message is required');
                  return;
                }
                loyaltyApi
                  .createAnnouncement({
                    message: annForm.message.trim(),
                    type: annForm.type,
                    linkUrl: annForm.linkUrl || undefined,
                    sortOrder: annForm.sortOrder,
                    startsAt: annForm.startsAt || undefined,
                    endsAt: annForm.endsAt || undefined,
                  })
                  .then(() => {
                    toast.success('Announcement created');
                    setAnnForm({
                      message: '',
                      type: 'GENERAL',
                      linkUrl: '',
                      sortOrder: 0,
                      startsAt: '',
                      endsAt: '',
                    });
                    qc.invalidateQueries({ queryKey: ['loyalty-announcements'] });
                  })
                  .catch((e: { response?: { data?: { message?: string } } }) =>
                    toast.error(e.response?.data?.message || 'Create failed')
                  );
              }}
            >
              Add announcement
            </Button>
          </div>
          <ul className="space-y-2 text-sm">
            {(announcements as PatientPortalAnnouncement[] | undefined)?.map((a) => (
              <li
                key={a.id}
                className="border rounded-lg p-4 bg-white flex flex-col sm:flex-row sm:items-start gap-3 justify-between"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold uppercase text-teal-800 bg-teal-50 px-2 py-0.5 rounded">
                      {ANNOUNCEMENT_TYPE_LABELS[a.type]}
                    </span>
                    {!a.isActive && (
                      <span className="text-xs text-muted-foreground">Inactive</span>
                    )}
                    <span className="text-xs text-muted-foreground">Order {a.sortOrder}</span>
                  </div>
                  <p className="font-medium">{a.message}</p>
                  {a.linkUrl && (
                    <p className="text-xs text-muted-foreground truncate">{a.linkUrl}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      loyaltyApi
                        .updateAnnouncement(a.id, { isActive: !a.isActive })
                        .then(() => {
                          toast.success(a.isActive ? 'Hidden from app' : 'Now visible in app');
                          qc.invalidateQueries({ queryKey: ['loyalty-announcements'] });
                        })
                    }
                  >
                    {a.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => {
                      if (!confirm('Delete this announcement?')) return;
                      loyaltyApi.deleteAnnouncement(a.id).then(() => {
                        toast.success('Deleted');
                        qc.invalidateQueries({ queryKey: ['loyalty-announcements'] });
                      });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
            {!announcements?.length && (
              <li className="text-muted-foreground border rounded-lg p-6 text-center">
                No announcements yet. Add one to show in the patient app home banner.
              </li>
            )}
          </ul>
        </div>
      )}

      {tab === 'campaigns' && (
        <div className="space-y-6">
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="font-semibold">New campaign</h3>
            <Input placeholder="Title" value={campForm.title} onChange={(e) => setCampForm((c) => ({ ...c, title: e.target.value }))} />
            <Input placeholder="Message body" value={campForm.body} onChange={(e) => setCampForm((c) => ({ ...c, body: e.target.value }))} />
            <div className="flex gap-2">
              <Button
                onClick={() =>
                  loyaltyApi.createCampaign(campForm).then(() => {
                    toast.success('Campaign created');
                    qc.invalidateQueries({ queryKey: ['loyalty-campaigns'] });
                  })
                }
              >
                Create
              </Button>
              <Button
                variant="secondary"
                onClick={() =>
                  loyaltyApi.broadcastCampaigns().then(() => toast.success('Broadcast sent to wallet patients'))
                }
              >
                Broadcast active campaigns
              </Button>
            </div>
          </div>
          <ul className="space-y-2 text-sm">
            {(campaigns as Array<{ id: string; title: string; channel: string; _count?: { deliveries: number } }>)?.map((c) => (
              <li key={c.id} className="border rounded p-3 flex justify-between">
                <span>{c.title}</span>
                <span className="text-muted-foreground">{c.channel} · {c._count?.deliveries ?? 0} sent</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'challenges' && (
        <div className="space-y-6">
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="font-semibold">New wellness challenge</h3>
            <Input placeholder="Title" value={chalForm.title} onChange={(e) => setChalForm((c) => ({ ...c, title: e.target.value }))} />
            <Button
              onClick={() =>
                loyaltyApi.createChallenge(chalForm).then(() => {
                  toast.success('Challenge created');
                  qc.invalidateQueries({ queryKey: ['loyalty-challenges'] });
                })
              }
            >
              Create challenge
            </Button>
          </div>
          <ul className="space-y-2 text-sm">
            {(challenges as Array<{ id: string; title: string; targetCount: number; pointsReward: number; _count?: { progress: number } }>)?.map((ch) => (
              <li key={ch.id} className="border rounded p-3">
                {ch.title} — target {ch.targetCount}, reward {ch.pointsReward} pts · {ch._count?.progress ?? 0} enrolled
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'tiers' && (
        <ul className="space-y-3">
          {data?.tiers?.map((t) => (
            <li key={t.id} className="border rounded-lg p-4">
              <p className="font-semibold">{t.displayName}</p>
              <p className="text-sm text-muted-foreground">
                Min spend ₹{t.minAnnualSpend} · Min visits {t.minVisitsPerYear} · Checkup discount {t.checkupDiscountPct}%
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="border rounded-lg p-4 bg-white">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold text-teal-800">{value}</p>
    </div>
  );
}
